import React from 'react';

import {useI18n} from '@udemy/i18n';
import {SpotIllustration} from '@udemy/illustrations';
import {Button} from '@udemy/react-core-components';
import {Modal} from '@udemy/react-dialog-components';
import {Checkbox} from '@udemy/react-form-components';
import {TakeOverWizard} from '@udemy/react-structure-components';

import styles from '../pop-quiz-modal.module.less';
import {QuizData} from './types';
import {renderUdemyLogo} from './utils';

interface PopQuizResultsPhaseProps {
    isOpen: boolean;
    quizData: QuizData;
    correctCount: number;
    totalQuestions: number;
    stopShowing: boolean;
    setStopShowing: (v: boolean) => void;
    onExit: () => void;
    onContinueLearning: () => void;
}

export function PopQuizResultsPhase({
    isOpen,
    quizData,
    correctCount,
    totalQuestions,
    stopShowing,
    setStopShowing,
    onExit,
    onContinueLearning,
}: PopQuizResultsPhaseProps) {
    const {gettext, interpolate} = useI18n();

    const pct = totalQuestions > 0 ? correctCount / totalQuestions : 0;
    const {resultTitle, resultIconName, resultBgClass} = (() => {
        if (pct === 1) {
            return {
                resultTitle: gettext('Nailed it'),
                resultIconName: 'goal-crushing' as const,
                resultBgClass: styles['results-bg-perfect'],
            };
        }
        if (pct >= 0.8) {
            return {
                resultTitle: gettext('Solid'),
                resultIconName: 'thank-you' as const,
                resultBgClass: styles['results-bg-solid'],
            };
        }
        if (pct >= 0.5) {
            return {
                resultTitle: gettext('Not bad'),
                resultIconName: 'personal-plan' as const,
                resultBgClass: styles['results-bg-notbad'],
            };
        }
        return {
            resultTitle: gettext('Tough one'),
            resultIconName: 'error' as const,
            resultBgClass: styles['results-bg-toughone'],
        };
    })();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onExit}
            title=""
            renderTitle={() => [
                'results-modal-label',
                <span key="t" id="results-modal-label" className="ud-sr-only">
                    {gettext('Results')}
                </span>,
            ]}
            className={styles['quiz-modal']}
        >
            <TakeOverWizard
                fullScreenHeight={false}
                headerProps={{
                    renderLogo: renderUdemyLogo,
                    centerChildren: gettext('Results'),
                    rightChildren: (
                        <Button udStyle="ghost" size="medium" onClick={onExit}>
                            {gettext('Exit')}
                        </Button>
                    ),
                }}
                footerProps={{
                    leftChildren: (
                        <Checkbox
                            checked={stopShowing}
                            onChange={(e) => setStopShowing(e.target.checked)}
                        >
                            {gettext('Stop showing me this')}
                        </Checkbox>
                    ),
                    rightChildren: (
                        <div className={styles['footer-actions']}>
                            <Button udStyle="ghost" size="medium" onClick={onExit}>
                                {gettext('Not now')}
                            </Button>
                            <Button udStyle="primary" size="medium" onClick={onContinueLearning}>
                                {gettext('Keep learning')}
                            </Button>
                        </div>
                    ),
                }}
            >
                <div className={styles['results-content']}>
                    <div className={`${styles['results-container']} ${resultBgClass}`}>
                        <p className={`ud-heading-xl ${styles['results-title']}`}>{resultTitle}</p>
                        <div className={styles['results-icon']}>
                            <SpotIllustration name={resultIconName} width={96} height={96} alt="" />
                        </div>
                        <div className={styles['score-card']}>
                            <p className="ud-heading-xl">
                                {interpolate(
                                    gettext('%(correct)s / %(total)s'),
                                    {correct: correctCount, total: totalQuestions},
                                    true,
                                )}
                            </p>
                            <p className={`ud-text-md ${styles['score-label']}`}>
                                {gettext('Questions answered correctly')}
                            </p>
                        </div>
                    </div>
                    <p className={`ud-text-md ${styles['resume-prompt']}`}>
                        {interpolate(
                            gettext(
                                "You're %(pct)s% through %(courseTitle)s. Ready to keep going?",
                            ),
                            {
                                pct: quizData.courseProgress,
                                courseTitle: quizData.courseTitle,
                            },
                            true,
                        )}
                    </p>
                </div>
            </TakeOverWizard>
        </Modal>
    );
}
